import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { User } from 'src/app/features/user/user';
import { Paginator } from 'src/app/shared/models/paginator';
import { TableHeader } from 'src/app/shared/models/table';
import { PageableService } from 'src/app/shared/services/pageable.service';
import { environment } from 'src/environments/environment';
import { ReportGroup } from '../admin/report-group/report-group';
@Injectable({
    providedIn: 'root',
})
export class UserService {
    public usersUrl = `${environment.urlBase}/user/`;

    constructor(private http: HttpClient, private pageableService: PageableService) { }

    /**
     * Creates a user
     * @param {User} user
     * @returns {Observable<User>}
     */
    createUser(user: User): Observable<User> {
        return this.http.post<User>(this.usersUrl, user);
    }

    /**
     * Updates an already created user
     * @param {User} user
     * @returns {Observable<User>}
     */
    updateUser(user: User): Observable<User> {
        return this.http.put<User>(this.usersUrl, user);
    }

    /**
     * Gets all users by page
     * It can get some params in order to filter or sort the data
     * @param {number} page
     * @param {number} pageQuantity
     * @param {string} header
     * @param {string} filter
     * @param {string} order
     * @returns {Observable<Paginator<User>>}
     */
    getUsers(page: number, pageQuantity: number, header?: string, filter?: string, order?: string): Observable<Paginator<User>> {
        return this.pageableService.getData<User>(this.usersUrl, page, pageQuantity, header, filter, order);
    }

    /**
     * Gets all users by page and filters
     * @param {BehaviorSubject} search$
     * @param {number} page
     * @param {number} pageQuantity
     * @returns { Observable<Paginator<User>>}
     */
    getUsersByFilters(search$: BehaviorSubject<TableHeader | undefined>, page: number, pageQuantity: number): Observable<Paginator<User>> {
        return this.pageableService.searchData<User>(search$, page, pageQuantity, this.usersUrl);
    }

    /**
     * Gets the user uuid and changes the value of status property
     * @param {string} uuid
     * @returns {Observable<User>}
     */
    changeUserStatus(uuid: string): Observable<User> {
        return this.http.put<User>(`${this.usersUrl}actualizar/${uuid}`, null);
    }

    /**
     * Updates the user info
     * @param {User} user
     * @returns {Observable<User>}
     */
    updateProfile(user: User): Observable<User> {
        return this.http.put<User>(`${this.usersUrl}actualizar-informacion`, user);
    }

    /**
     * This function associates a list of report groups to an user
     * @param {string} userUuid
     * @param {ReportGroup[]} reportGroups
     * @returns {Observable<ReportGroup[]>}
     */
    setReportGroups(userUuid: string, reportGroups: ReportGroup[]): Observable<ReportGroup[]> {
        return this.http.put<ReportGroup[]>(`${environment.urlBase}/grupo-reporte/usuario/${userUuid}`, reportGroups);
    }

    /**
     * Gets all report groups associated with the current user
     * @param {string} userUuid
     * @returns {Observable<ReportGroup[]>}
     */
    getReportGroups(userUuid: string): Observable<ReportGroup[]> {
        return this.http.get<ReportGroup[]>(`${environment.urlBase}/grupo-reporte/usuario/${userUuid}`);
    }

    /**
     * Deletes a user by their UUID.
     * @param {string} userUuid
     * @returns {Observable<void>}
     */
    deleteUser(userUuid: string): Observable<User> {
        return this.http.delete<User>(`${this.usersUrl}${userUuid}`);
    }
}
